import { formatInTimeZone } from "date-fns-tz";
import React, { useMemo } from "react";
import { DetectionZoneOccupancy, Facility, IDetectionZoneOccupancy } from "../../models";
import { getTargetTypeDisplayName } from "../../models/Shared.TargetType.gen";
import { CheckboxInput, SortableTable, SortableTableHeaderType } from "@app/dashboard/src/components/shared";

export enum OccupancyViewMode {
    Facility = "facility",
    Device = "device"
}

type ExtendedOccupancy = (DetectionZoneOccupancy | IDetectionZoneOccupancy) & {
    zoneName: string;
    zoneNum: number;
    deviceName: string;
};

type RecentOccupancyTableProps = {
    mode: OccupancyViewMode;
    facility: Facility;
    data: (DetectionZoneOccupancy | IDetectionZoneOccupancy)[];
    isLoading?: boolean;
};

export const RecentOccupancyTable: React.FC<RecentOccupancyTableProps> = ({ mode, facility, data, isLoading }) => {
    const isDeviceMode = mode === OccupancyViewMode.Device;
    const getDeviceNameForZone = (zone?: ExtendedOccupancy) => zone?.deviceName ?? "";

    const zone = facility.localTimezoneId ?? Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).resolvedOptions().timeZone;
    const timeZone = useMemo(() => {
        return formatInTimeZone(new Date(), zone, "z");
    }, [zone]);

    const extendedData = useMemo((): ExtendedOccupancy[] => {
        if (!data?.length) { return [] };
        return data.map(datum => ({
            ...datum,
            zoneName: facility.detectionZones?.find(z => z.id === datum?.detectionZoneId)?.name ?? "",
            zoneNum: -1,
            deviceName: facility.devices?.find(d => d.id === datum.deviceId)?.name ?? ""
        }))
            .map(datum => {
                const match = datum.zoneName.match(/\d+/);
                if (match) {
                    datum.zoneNum = parseInt(match[0], 10);
                }
                return datum;
            });
    }, [data, facility.detectionZones, facility.devices]);

    const hasNums = extendedData.every(x => x.zoneNum > -1);

    const columns = useMemo((): SortableTableHeaderType<ExtendedOccupancy>[] => {
        const cols: SortableTableHeaderType<ExtendedOccupancy>[] = [
            { dataKey: "detectionZoneId", header: "Detection Zone", width: 100, renderFunc: (zi) => `${facility.detectionZones?.find(z => z.id === zi?.detectionZoneId)?.name}` },
            { dataKey: "zoneNum", header: "Detection Zone Number", width: 100, renderFunc: (zi) => hasNums ? zi?.zoneNum : null },
            { dataKey: "isOccupied", header: "Occupied", width: 100, renderFunc: (zi) => <CheckboxInput checked={zi?.isOccupied} readOnly={true} /> },
            { dataKey: "targetType", header: "Target Type", width: 150, renderFunc: (target) => getTargetTypeDisplayName(target!.targetType) },
        ];

        if (!isDeviceMode) {
            cols.push({ dataKey: "deviceName", header: "Detection Device", width: 175, renderFunc: getDeviceNameForZone });
        }

        cols.push({ dataKey: "dateDetected", header: `Last Change (${timeZone})`, width: 135, renderFunc: (target) => target?.dateDetected ? formatInTimeZone(new Date(target?.dateDetected as unknown as string), zone, "MMM d, HH:mm") : "" });

        return cols;
    }, [facility.detectionZones, hasNums, isDeviceMode, timeZone, zone]);

    return (
        <SortableTable
            data={extendedData}
            initialSortKey={hasNums ? "zoneNum" : "detectionZoneId"}
            initialSortDir="asc"
            isLoading={isLoading}
            columns={columns}
        />
    );
};
